import React, { useState, useEffect } from 'react';

const ConversationsTab = ({
  conversations, selectedConversation,
  fetchConversationDetail, handoffConversation,
  respondConversation, resolveConversation,
  getStatusBadge, formatDate, onRefresh
}) => {
  const [replyText, setReplyText] = useState('');
  const [handoffNote, setHandoffNote] = useState('');
  const [replyMode, setReplyMode] = useState(null); // 'reply' | 'handoff' | null
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const filtered = conversations.filter(c => {
    const name = `${c.customer_first_name || ''} ${c.customer_last_name || ''}`.toLowerCase();
    const matchSearch = !search || name.includes(search.toLowerCase()) || (c.channel || '').includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || c.resolution === filterStatus;
    return matchSearch && matchStatus;
  });

  const channelColor = (ch) => {
    const m = { whatsapp: '#25D366', instagram: '#E4405F', web_chat: '#6366f1', sms: '#F59E0B' };
    return m[ch] || '#6B7280';
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    await respondConversation(selectedConversation.id, replyText);
    setReplyText(''); setReplyMode(null);
  };

  const handleHandoff = async () => {
    await handoffConversation(selectedConversation.id, handoffNote || '');
    setHandoffNote(''); setReplyMode(null);
  };

  return (
    <div className="conv-tab">
      {/* Header */}
      <div className="conv-header">
        <div>
          <h2>💬 Konuşmalar</h2>
          <p style={{ margin:0, color:'#6B7280', fontSize:13 }}>{conversations.length} konuşma</p>
        </div>
        <button className="conv-refresh-btn" onClick={onRefresh}>🔄 Yenile</button>
      </div>

      {/* Filters */}
      <div className="conv-filters">
        <div className="conv-search-wrap">
          <span className="conv-search-icon">🔍</span>
          <input
            className="conv-search"
            placeholder="Müşteri veya kanal ara..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="conv-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">Tüm Durumlar</option>
          <option value="active">Aktif</option>
          <option value="auto_resolved">Çözüldü</option>
          <option value="handed_to_human">Personelde</option>
          <option value="pending">Bekliyor</option>
        </select>
      </div>

      {/* Conversation List */}
      {filtered.length === 0 ? (
        <div className="conv-empty">
          <div style={{ fontSize:56, marginBottom:16 }}>💬</div>
          <h3>Konuşma bulunamadı</h3>
          <p>Webhook bağlandığında konuşmalar burada görünecek</p>
        </div>
      ) : (
        <div className="conv-list">
          {filtered.map(conv => (
            <div
              key={conv.id}
              className={`conv-card ${selectedConversation?.id === conv.id ? 'selected' : ''}`}
              onClick={() => fetchConversationDetail(conv.id)}
            >
              <div className="conv-card-left">
                <div className="conv-avatar" style={{ background: `${channelColor(conv.channel)}22`, border: `1px solid ${channelColor(conv.channel)}44` }}>
                  {conv.channel === 'whatsapp' ? '💬' : conv.channel === 'instagram' ? '📸' : conv.channel === 'web_chat' ? '🌐' : '📱'}
                </div>
                <div className="conv-card-info">
                  <div className="conv-customer">{conv.customer_first_name} {conv.customer_last_name}</div>
                  <div className="conv-intent">{conv.detected_intent || 'Tanımlanmamış intent'}</div>
                  <div className="conv-time">{formatDate(conv.last_message_at)}</div>
                </div>
              </div>
              <div className="conv-card-right">
                <span className="conv-channel-tag" style={{ background: `${channelColor(conv.channel)}18`, color: channelColor(conv.channel), borderColor: `${channelColor(conv.channel)}30` }}>
                  {conv.channel}
                </span>
                {getStatusBadge(conv.resolution)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Panel */}
      {selectedConversation && (
        <div className="conv-detail-panel">
          <div className="conv-detail-header">
            <div>
              <h3>Konuşma Detayı</h3>
              <div className="conv-detail-meta">
                <span>{selectedConversation.customer_first_name} {selectedConversation.customer_last_name}</span>
                <span className="meta-sep">·</span>
                <span style={{ color: channelColor(selectedConversation.channel) }}>{selectedConversation.channel}</span>
                <span className="meta-sep">·</span>
                <span>{selectedConversation.detected_intent || '-'}</span>
              </div>
            </div>
            <div className="conv-detail-actions">
              {getStatusBadge(selectedConversation.resolution)}
            </div>
          </div>

          <div className="conv-action-btns">
            <button
              className={`conv-act-btn ${replyMode === 'reply' ? 'active-reply' : ''}`}
              onClick={() => setReplyMode(replyMode === 'reply' ? null : 'reply')}
            >
              💬 Yanıtla
            </button>
            <button
              className={`conv-act-btn ${replyMode === 'handoff' ? 'active-handoff' : ''}`}
              onClick={() => setReplyMode(replyMode === 'handoff' ? null : 'handoff')}
            >
              👤 Personele Devret
            </button>
            <button
              className="conv-act-btn success"
              onClick={() => resolveConversation(selectedConversation.id)}
            >
              ✅ Çözümle
            </button>
          </div>

          {replyMode === 'reply' && (
            <div className="conv-input-area">
              <textarea
                className="conv-textarea"
                placeholder="Müşteriye yanıt yazın..."
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                rows={3}
              />
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button className="conv-cancel-btn" onClick={() => { setReplyMode(null); setReplyText(''); }}>İptal</button>
                <button className="conv-send-btn" onClick={handleReply} disabled={!replyText.trim()}>📤 Gönder</button>
              </div>
            </div>
          )}

          {replyMode === 'handoff' && (
            <div className="conv-input-area">
              <textarea
                className="conv-textarea"
                placeholder="Personele not bırakın (opsiyonel)..."
                value={handoffNote}
                onChange={e => setHandoffNote(e.target.value)}
                rows={3}
              />
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button className="conv-cancel-btn" onClick={() => { setReplyMode(null); setHandoffNote(''); }}>İptal</button>
                <button className="conv-handoff-btn" onClick={handleHandoff}>👤 Devret</button>
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        .conv-tab { display:flex; flex-direction:column; gap:20px; }

        .conv-header { display:flex; justify-content:space-between; align-items:flex-start; }
        .conv-header h2 { margin:0 0 2px; font-size:22px; font-weight:700; color:#F9FAFB; }
        .conv-refresh-btn { padding:10px 18px; background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#E5E7EB; font-size:13px; cursor:pointer; transition:all 0.2s; }
        .conv-refresh-btn:hover { background:rgba(255,255,255,0.1); }

        .conv-filters { display:flex; gap:12px; }
        .conv-search-wrap { position:relative; flex:1; }
        .conv-search-icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); font-size:13px; }
        .conv-search { width:100%; padding:10px 14px 10px 36px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#E5E7EB; font-size:14px; outline:none; transition:border-color 0.2s; box-sizing:border-box; }
        .conv-search:focus { border-color:rgba(96,165,250,0.4); }
        .conv-select { padding:10px 14px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#E5E7EB; font-size:14px; outline:none; cursor:pointer; }
        .conv-select option { background:#13131a; }

        .conv-empty { text-align:center; padding:80px 20px; color:#9CA3AF; background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.07); border-radius:18px; }
        .conv-empty h3 { color:#E5E7EB; font-size:18px; margin:0 0 8px; }
        .conv-empty p { margin:0; font-size:14px; }

        .conv-list { display:flex; flex-direction:column; gap:8px; }

        .conv-card { background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.07); border-radius:14px; padding:16px; cursor:pointer; transition:all 0.2s; display:flex; justify-content:space-between; align-items:center; gap:12px; backdrop-filter:blur(16px); }
        .conv-card:hover { border-color:rgba(96,165,250,0.2); background:rgba(96,165,250,0.04); }
        .conv-card.selected { border-color:rgba(96,165,250,0.35); background:rgba(96,165,250,0.07); }

        .conv-card-left { display:flex; align-items:center; gap:12px; flex:1; min-width:0; }
        .conv-avatar { width:42px; height:42px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:20px; flex-shrink:0; }
        .conv-card-info { min-width:0; }
        .conv-customer { font-size:14px; font-weight:600; color:#E5E7EB; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .conv-intent { font-size:12px; color:#6B7280; margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .conv-time { font-size:11px; color:#4B5563; margin-top:3px; }

        .conv-card-right { display:flex; flex-direction:column; align-items:flex-end; gap:6px; flex-shrink:0; }
        .conv-channel-tag { padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; border:1px solid; text-transform:uppercase; letter-spacing:0.04em; }

        .conv-detail-panel { background:rgba(17,17,24,0.8); border:1px solid rgba(255,255,255,0.1); border-radius:18px; padding:24px; display:flex; flex-direction:column; gap:16px; backdrop-filter:blur(20px); }
        .conv-detail-header { display:flex; justify-content:space-between; align-items:flex-start; }
        .conv-detail-header h3 { margin:0 0 4px; font-size:18px; color:#F9FAFB; }
        .conv-detail-meta { display:flex; align-items:center; gap:8px; font-size:13px; color:#6B7280; flex-wrap:wrap; }
        .meta-sep { color:#374151; }

        .conv-action-btns { display:flex; gap:8px; flex-wrap:wrap; }
        .conv-act-btn { padding:9px 16px; background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.1); border-radius:10px; color:#E5E7EB; font-size:13px; font-weight:600; cursor:pointer; transition:all 0.2s; }
        .conv-act-btn:hover { background:rgba(255,255,255,0.1); }
        .conv-act-btn.active-reply { background:rgba(96,165,250,0.15); border-color:rgba(96,165,250,0.3); color:#60A5FA; }
        .conv-act-btn.active-handoff { background:rgba(251,191,36,0.12); border-color:rgba(251,191,36,0.25); color:#FBBF24; }
        .conv-act-btn.success { background:rgba(52,211,153,0.12); border-color:rgba(52,211,153,0.25); color:#34D399; }

        .conv-input-area { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.07); border-radius:12px; padding:16px; display:flex; flex-direction:column; gap:12px; }
        .conv-textarea { padding:12px 14px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#E5E7EB; font-size:14px; resize:vertical; outline:none; font-family:inherit; transition:border-color 0.2s; }
        .conv-textarea:focus { border-color:rgba(96,165,250,0.4); }
        .conv-cancel-btn { padding:8px 16px; background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.08); border-radius:9px; color:#9CA3AF; font-size:13px; cursor:pointer; }
        .conv-send-btn { padding:8px 18px; background:linear-gradient(135deg,#1d4ed8,#60A5FA); border:none; border-radius:9px; color:#fff; font-size:13px; font-weight:600; cursor:pointer; }
        .conv-send-btn:disabled { opacity:0.5; cursor:not-allowed; }
        .conv-handoff-btn { padding:8px 18px; background:linear-gradient(135deg,#d97706,#FBBF24); border:none; border-radius:9px; color:#fff; font-size:13px; font-weight:600; cursor:pointer; }
      `}</style>
    </div>
  );
};

export default ConversationsTab;
